import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/apiClient";

// ReferralThanksToast — the "thanks for the referral" dopamine signal.
//
// On every auth session (each time `user` becomes truthy), pull any
// UNSEEN rows from /api/notifications/referrals, fire a sonner toast
// per row ("🎉 someone joined via GlobalEyes.TV/{yourhandle}"), then
// immediately POST /notifications/mark-referrals-seen so they don't
// re-fire next page-load. If the user has 5+ unseen we collapse to
// a single toast so we don't ladder-spam them.
//
// We also poll every 60s while the tab is foregrounded — that's slow
// enough not to hammer the API and fast enough that a referrer who
// has the dashboard open while talking on X sees the toast within
// the same conversation.
//
// Mounted in AppShell so it's app-wide, not dashboard-only — the
// dopamine should land wherever the user happens to be.
const POLL_INTERVAL_MS = 60_000;

// Inner component for the per-row toast. Lives inside the toast
// callback so we can manage its own "following / followed / failed"
// state without polluting the parent component. The toast is dismissed
// by sonner automatically after `duration`; the local state is just
// for the in-toast visual feedback during the brief network call.
const ThanksToastBody = ({ toastId, ownerHandle, joinerUsername, onView, dismiss }) => {
  // `idle` | `working` | `followed` | `already` | `failed`
  const [state, setState] = useState("idle");

  const doFollow = async () => {
    if (state !== "idle") return;
    setState("working");
    try {
      await api.post(`/users/${joinerUsername}/follow`);
      setState("followed");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      // "Already following this channel" returns 400 — we treat that
      // as a successful state from the user's POV (they're connected).
      if (/already following/i.test(detail)) {
        setState("already");
      } else {
        setState("failed");
      }
    }
  };

  return (
    <div className="bg-gray-900 border border-emerald-500/30 rounded-lg shadow-lg px-4 py-3 min-w-[320px] max-w-md">
      <p className="text-emerald-300 font-semibold text-sm">
        🎉 @{joinerUsername} just joined
      </p>
      <p className="text-gray-400 text-xs mt-0.5">
        Via {ownerHandle} — your score went up.
      </p>
      <div className="flex items-center gap-2 mt-2.5">
        <button
          type="button"
          onClick={doFollow}
          disabled={state === "working" || state === "followed" || state === "already"}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
            state === "followed" || state === "already"
              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 cursor-default"
              : state === "failed"
                ? "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                : "bg-emerald-500 hover:bg-emerald-400 text-gray-900 disabled:opacity-60"
          }`}
          data-testid="toast-follow-back-btn"
        >
          {state === "working" && <span className="h-3 w-3 animate-spin border border-current border-t-transparent rounded-full" />}
          {(state === "followed" || state === "already") && <Check className="h-3.5 w-3.5" />}
          {state === "idle" && <UserPlus className="h-3.5 w-3.5" />}
          {state === "failed" && <UserPlus className="h-3.5 w-3.5" />}
          {state === "idle" && "Follow back"}
          {state === "working" && "Following…"}
          {state === "followed" && "Following"}
          {state === "already" && "Following"}
          {state === "failed" && "Retry"}
        </button>
        <button
          type="button"
          onClick={() => { onView(); dismiss(); }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
          data-testid="toast-view-profile-btn"
        >
          <ExternalLink className="h-3 w-3" /> View profile
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="ml-auto text-gray-500 hover:text-gray-300 text-[11px]"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

const ReferralThanksToast = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    if (!token || !user?.id) {
      lastUserIdRef.current = null;
      return undefined;
    }
    // Reset state when the user changes (log out / log in different
    // account) — otherwise we'd skip the first-fetch dedupe and
    // double-fire the toast for the new user.
    if (lastUserIdRef.current !== user.id) {
      lastUserIdRef.current = user.id;
    }

    let cancelled = false;
    const fetchAndToast = async () => {
      try {
        const r = await api.get("/notifications/referrals");
        const items = r.data?.items || [];
        if (cancelled || items.length === 0) return;

        const ownUrl = `GlobalEyes.TV/${user.username}`;
        if (items.length >= 5) {
          // Collapse the burst so we don't ladder-spam. Action button
          // jumps to the leaderboard so the producer can savour the
          // climb (no single joiner to deep-link to in this case).
          toast.success(
            `🎉 ${items.length} new referrals via ${ownUrl}`,
            {
              description: "Check the leaderboard to see your rank climb.",
              duration: 8000,
              action: {
                label: "Leaderboard",
                onClick: () => navigate("/leaderboard"),
              },
            },
          );
        } else {
          // One toast per — staggered slightly so they stack nicely.
          // We use toast.custom so each can host BOTH a primary
          // "Follow back" action (the compounding move — turns a
          // referral into a mutual connection) AND a secondary
          // "View profile" deep-link, plus an explicit Dismiss. Sonner
          // tops out at one action+cancel for its standard toast, so
          // custom is the cleanest way to expose multiple CTAs.
          items.forEach((item, idx) => {
            setTimeout(() => {
              toast.custom((t) => (
                <ThanksToastBody
                  toastId={t}
                  ownerHandle={ownUrl}
                  joinerUsername={item.new_user_username}
                  onView={() => navigate(`/${item.new_user_username}`)}
                  dismiss={() => toast.dismiss(t)}
                />
              ), { duration: 9000 });
            }, idx * 400);
          });
        }

        // Clear server-side so we don't refire on the next poll.
        await api.post("/notifications/mark-referrals-seen").catch(() => {
          /* swallow — worst case we refire once, which is acceptable */
        });
      } catch {
        // Network blip — silent retry on next poll.
      }
    };

    // Initial fetch on mount, then poll while the tab is open and
    // foregrounded. document.hidden check avoids burning quota
    // when the user has the tab in the background.
    fetchAndToast();
    const interval = setInterval(() => {
      if (!document.hidden) fetchAndToast();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token, user?.id, user?.username]);

  return null;
};

export default ReferralThanksToast;
